import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getBitacora } from "../../../apis";
import { Box, Fade, Stack, Typography, useTheme } from "@mui/material";
import { EGPageHeader } from "../../Title/PageHeader/EGPageHeader";
import { TableSkeleton } from "../../Loading/Table/Table";
import { EGPaginator } from "../../Paginator/Paginator";
import { BitacoraTable } from "./BitacoraTable/BitacoraTable";
import { Filters } from "./Filters";
import dayjs from "dayjs";
import { EGIconButton } from "../Button/IconButton/IconButton";

export const Bitacora = () => {
  // ---> Custom Hooks <--- //
  const theme = useTheme();
  const { t } = useTranslation("report");
  // Bitacora filters by default
  const defaultFilters = useMemo(
    () => ({
      date_range: {
        active: true,
        start: dayjs().subtract(7, "day").format("YYYY-MM-DD"), // Last 7 days
        end: dayjs().format("YYYY-MM-DD"), // Today
      },
      user_email: { active: false, value: "" },
      path: { active: false, value: "" },
      folio: { active: false, value: "" },
    }),
    []
  );

  // ---> Initial states <--- //
  const [isLoading, setIsLoading] = useState(true);
  const [bitacoraList, setBitacoraList] = useState([]);
  // Server pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  // Initia filters
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  /** cleanedFilters
   * Removes inactive or empty fields (does not send user_email if inactive/empty).
   * Transforms data into the format expected by the API (such as dates in text).
   * Applies a fallback if there is no active range (last 7 days).
   **/
  const cleanedFilters = useMemo(() => {
    const filters = {};
    if (appliedFilters.date_range.active) {
      filters.start_date = appliedFilters.date_range.start;
      filters.end_date = appliedFilters.date_range.end;
    } else {
      filters.start_date = dayjs().subtract(7, "day").format("YYYY-MM-DD");
      filters.end_date = dayjs().format("YYYY-MM-DD");
    }
    if (appliedFilters.user_email.active && appliedFilters.user_email.value) {
      filters.user_email = appliedFilters.user_email.value;
    }
    if (appliedFilters.path.active && appliedFilters.path.value) {
      filters.path = appliedFilters.path.value;
    }
    if (appliedFilters.folio.active && appliedFilters.folio.value) {
      filters.folio = appliedFilters.folio.value;
    }
    return filters;
  }, [appliedFilters]);

  // Determine if there are active filters
  const hasActiveFilters = useMemo(() => {
    const df = defaultFilters;
    const af = appliedFilters;

    const dateChanged =
      af.date_range.active !== df.date_range.active ||
      af.date_range.start !== df.date_range.start ||
      af.date_range.end !== df.date_range.end;

    return (
      dateChanged ||
      (af.user_email.active && af.user_email.value !== "") ||
      (af.path.active && af.path.value !== "") ||
      (af.folio.active && af.folio.value !== "")
    );
  }, [appliedFilters, defaultFilters]);

  // Calculation of navigation pages
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // API call with/without filters
  const fetchBitacora = useCallback(
    async (pageParam = page, sizeParam = rowsPerPage) => {
      setIsLoading(true);
      try {
        const params = {
          ...cleanedFilters,
          page: pageParam,
          page_size: sizeParam,
        };

        const data = await getBitacora(params);

        setBitacoraList(data?.results ?? []);
        setTotalCount(data?.count ?? 0);
      } catch (error) {
        console.error("[Bitacora] fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [page, rowsPerPage, cleanedFilters]
  );

  // Fetch when change page/rowsPerPage/filtros
  useEffect(() => {
    fetchBitacora(page, rowsPerPage);
  }, [fetchBitacora, page, rowsPerPage]);

  // Page onChange event
  const handlePageChange = (_evt, newPage) => {
    setPage(newPage);
  };
  // rows per page onChange event
  const handleRowsPerPageChange = (evt) => {
    const newSize = Number(evt.target.value);
    setRowsPerPage(newSize);
    setPage(1); // reset a primera página
  };

  // Apply filters
  const applyFilters = (newFilters) => {
    setAppliedFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(1);
  };
  // Reset filters
  const resetFilters = () => {
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  return (
    <Box>
      <EGPageHeader
        breadcrumbItems={[
          { label: t("ReportsList.home"), iconName: "bold_home", route: "/" },
          { label: t("Bitacora.title") },
        ]}
        title={t("Bitacora.title")}
        subtitle={t("Bitacora.no_subject")}
        mainIconName="outline_document_filter"
        border
      />

      {/*  Top info bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          flexWrap: "wrap",
          bgcolor: theme.palette.background.paper,
          mt: 2,
          border: `${theme.palette.blueGray100.main} 1px solid`,
          py: { xs: 0.5, sm: 1 },
          px: { xs: 1, sm: 2 },
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography variant="h6" color="secondary" mr={1} py={0.5}>
          {`${t("ReportsList.showing")}: ${totalCount} ${t("Bitacora.result")}${
            totalCount === 1 ? "" : "s"
          }`}
        </Typography>
        {/* Filter search */}
        <Stack direction="row" spacing={1}>
          {hasActiveFilters && (
            <EGIconButton
              iconName="filter_remove"
              title={t("Filter.remove_filters")}
              iconColor="error"
              iconColorHover="white"
              colorHover="error"
              onClick={resetFilters}
            />
          )}
          <Filters
            initialFilters={appliedFilters}
            onApply={applyFilters}
            defaultFilters={useMemo(
              () => JSON.parse(JSON.stringify(defaultFilters)),
              []
            )}
          />
        </Stack>
      </Box>

      {/* Container table + paginator */}
      <Box
        sx={{
          mt: -0.3,
          pt: { xs: 1, sm: 1.5 },
          px: { xs: 1, sm: 1.5 },
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        className="dyTheme1 dyBorder1"
      >
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Fade in timeout={1000}>
            <Box>
              <BitacoraTable bitacoraList={bitacoraList} />

              <Stack
                component="nav"
                sx={{
                  borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                  alignItems: "center",
                  justifyContent: "space-around",
                  flexFlow: "row wrap",
                  py: 2,
                }}
                spacing={1}
              >
                <Stack sx={{ flexFlow: "row nowrap" }}>
                  <Typography p={0.5} fontSize={11}>
                    {t("ReportsList.per_page")}:
                  </Typography>
                  <select
                    className="paginator__select"
                    name="limit"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                  </select>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mt: 2, mb: 1 }}
                >
                  <EGPaginator
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                  />
                </Stack>

                <Typography p={0.5} fontSize={12}>
                  {`${t("ReportsList.results")}: ${
                    totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1
                  } ${t("ReportsList.to")} ${Math.min(
                    page * rowsPerPage,
                    totalCount
                  )} ${t("ReportsList.of")} ${totalCount}`}
                </Typography>
              </Stack>
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};
